/**
 * Component for our Analytics Customer page
 * used to show the comments of a customer on returned products
 * @component
 */

import {
  Col,
  Divider,
  Row,
  Table,
  Typography,
  message,
  Button, 
  Menu, 
  Dropdown
} from 'antd';
import axios from 'axios';
import React, { Component } from 'react';
import { IconTray } from '../../../../components/CustomReturnsIcons';
import Upgrade from '../../../../components/Upgrade';
import { SubscriptionContext } from '../../../../contexts/SubscriptionContext';
import { CSVLink } from 'react-csv';
import { Parser } from 'json2csv';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const { Text } = Typography;

const exportHeaders = [
  {
    label: 'Product Name',
    value: 'productName',
    key: 'productName',
  },
  {
    label: 'Variant Name',
    value: 'variantName',
    key: 'variantName',
  },
  {
    label: 'SKU',
    value: 'sku',
    key: 'sku',
  },
  {
    label: 'Return Number',
    value: 'Return.returnNumber',
    key: 'Return.returnNumber',
  },
  {
    label: 'Order Name',
    value: 'Return.orderName',
    key: 'Return.orderName',
  },
  {
    label: 'Comment',
    value: 'notes',
    key: 'notes',
  }
];

class Comments extends Component {
  constructor(props) {
    super(props);
    this.state = {
      data: [],
      dataCSVToBeExported: [],
      exportLoading: false,
      pdfLoading: false,
      timestamp : Date.now(),
    };
    this.csvLinkRef = React.createRef();
    this.printRef = React.createRef();
  }
  static contextType = SubscriptionContext;

  handleExportPdf = async () => {
    this.setState({ pdfLoading: true });
    
    try {
      if (this.state.data.length === 0) {
        this.setState({ pdfLoading: false });
        return message.error('There is no data to export as PDF');
      }

      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'pt',
        format: 'a4',
      });

      const pageWidth = pdf.internal.pageSize.width;
      pdf.setFontSize(16);
      const title = 'Customer Comments Report';
      const titleWidth = pdf.getStringUnitWidth(title) * pdf.internal.getFontSize() / pdf.internal.scaleFactor;
      pdf.text(title, (pageWidth - titleWidth) / 2, 40);
      
      pdf.setFontSize(12);
      const dateText = `Generated on: ${new Date().toLocaleDateString()}`;
      const dateWidth = pdf.getStringUnitWidth(dateText) * pdf.internal.getFontSize() / pdf.internal.scaleFactor;
      pdf.text(dateText, (pageWidth - dateWidth) / 2, 60);

      const headers = exportHeaders.map(h => h.label);
      const tableData = this.state.data.map(row => 
        exportHeaders.map(header => {
          const value = row[header.value];
          return value || '';
        })
      );

      const tableWidth = 690;
      const leftMargin = (pageWidth - tableWidth) / 2;

      autoTable(pdf, {
        head: [headers],
        body: tableData,
        startY: 80,
        margin: { 
          top: 80, 
          left: leftMargin, 
          right: leftMargin 
        },
        styles: {
          fontSize: 8,
          cellPadding: { top: 8, right: 5, bottom: 8, left: 5 }, 
          minCellHeight: 25, 
          halign: 'center', 
        },
        headStyles: {
          fillColor: [66, 139, 202],
          textColor: 255,
          fontStyle: 'bold',
          halign: 'left',
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245],
        },
        theme: 'striped',
        showHead: 'everyPage',
        columnStyles: {
          0: { cellWidth: 250, halign: 'left' },   
          1: { cellWidth: 60, halign: 'left' },  
          2: { cellWidth: 50, halign: 'left' },
          3: { cellWidth: 80, halign: 'left' }, 
          4: { cellWidth: 50, halign: 'left' },
          5: { cellWidth: 200, halign: 'left' }, 
        },
      });

      pdf.save(`RichReturns-Customer-Comments-${this.state.timestamp}.pdf`);
      message.success(`PDF downloaded successfully with ${this.state.data.length} records`);
    } catch (error) {
      console.error('PDF export error:', error);
      this.setState({ pdfLoading: false });
      return message.error('Failed to generate PDF');
    } finally {
      this.setState({ pdfLoading: false });
    }
  };

  async handleExportCsv() {
    this.setState({
      exportLoading: true,
    });

    try {
      if (this.state.data.length === 0) {
        this.setState({
          exportLoading: false,
        });
        return message.error('There is no data to export', 4);
      }

      const dataParser = new Parser({
        fields: exportHeaders,
        quote: '' 
      });

      const dataCSVToBeExported = dataParser.parse(this.state.data);

      this.setState({
        exportLoading: false,
        dataCSVToBeExported,
      });

      setTimeout(() => {
        if (this.csvLinkRef.current && this.state.dataCSVToBeExported.length > 0) {
          this.csvLinkRef.current.link.click();
          message.success('CSV downloaded successfully!', 2); 
          this.handleResetDataToBeExported();
        }
      }
      , 1000);
    } catch (err) {
      this.setState({
        exportLoading: false,
      });
      return message.error('Error creating report csv.', err);
    }
  }
  handleResetDataToBeExported = () => {
    this.setState({
      dataCSVToBeExported: [],
    });
  };

  async componentDidMount() {
    if (this.props.customerId) {
      try {
        const response = await axios.get(
          `/api/analytics/customers/${this.props.customerId}/comments`
        );
        const products = [...response.data.data];
        this.setState({
          data: products,
        });
      } catch (err) {
        return message.error(
          'something went wrong in getting the customer details',
          5
        );
      }
    }
  }

  async componentDidUpdate(prevProps) {
    if (prevProps.customerId !== this.props.customerId) {
      try {
        const response = await axios.get(
          `/api/analytics/customers/${this.props.customerId}/comments`
        );
        const products = [...response.data.data];
        this.setState({
          data: products,
        });
      } catch (err) {
        return message.error(
          'something went wrong in getting the customer details',
          5
        );
      }
    }
  }

  render() {
    // eslint-disable-next-line no-unused-vars
    const onChange = async (value) => {
      try {
        const response = await axios.get(
          `/api/analytics/customers/${value}/comments`
        );
        const products = [...response.data.data];
        this.props.updateCustomerDetails(value);
        this.setState({
          data: products,
        });
      } catch (err) {
        return message.error(
          'something went wrong in getting the customer details',
          5
        );
      }
    };
    const dataSource = this.state.data;
    const columns = [
      {
        title: 'Order Name',
        dataIndex: 'Return.orderName',
        onHeaderCell: () => ({
          'data-intercom-target': 'order-name-column-customer-comments-analytics-customers',
        }),
      },
      {
        title: 'Return Number',
        dataIndex: 'Return.returnNumber',
        onHeaderCell: () => ({
          'data-intercom-target': 'return-number-column-customer-comments-analytics-customers',
        }),
      },
      {
        title: 'Product Name',
        dataIndex: 'productName',
        onHeaderCell: () => ({
          'data-intercom-target': 'product-name-column-customer-comments-analytics-customers',
        }),
      },
      {
        title: 'Variant Name',
        dataIndex: 'variantName',
        onHeaderCell: () => ({
          'data-intercom-target': 'variant-name-column-customer-comments-analytics-customers',
        }),
      },
      {
        title: 'SKU',
        dataIndex: 'sku',
        onHeaderCell: () => ({
          'data-intercom-target': 'sku-column-customer-comments-analytics-customers',
        }),
      },
      {
        title: 'Comment',
        dataIndex: 'notes',
        onHeaderCell: () => ({
          'data-intercom-target': 'comment-column-customer-comments-analytics-customers',
        }),
      },
    ];

    return (
      <React.Fragment>
        {this.context.featureAnalyticsAdvanced ? (
          <React.Fragment>

            <Row type="flex" justify="space-between" align="middle" gutter={16} style={{ marginBottom: 20 }}>
              <Col
                xs={{ span: 24 }}
                sm={{ span: 18 }}
                md={{ span: 18 }}
                lg={{ span: 18 }}
              >
                <Text 
                  style={{ 
                    fontSize: 14,
                    display: 'flex',
                    alignItems: 'center',
                    height: '32px' // Match Button height
                  }}
                >
                  This report shows customer comments on returned products.
                </Text>
              </Col>
    
              <Col
                xs={{ span: 24 }}
                sm={{ span: 6 }}
                md={{ span: 6 }}
                lg={{ span: 6 }}
              >
                {this.state.dataCSVToBeExported.length > 0 && (
                  <CSVLink
                    data={this.state.dataCSVToBeExported}
                    filename={`RichReturns-Customer-Comments-${this.state.timestamp}.csv`}
                    className="hidden"
                    ref={this.csvLinkRef}
                  />
                )}
                
                {this.state.data.length > 0 && (
                  <Dropdown
                    overlay={
                      <Menu>
                        <Menu.Item key="csv" onClick={this.handleExportCsv.bind(this)} disabled={this.state.exportLoading}>
                          Export CSV
                        </Menu.Item>
                        <Menu.Item key="pdf" onClick={this.handleExportPdf} disabled={this.state.pdfLoading}>
                          Export PDF
                        </Menu.Item>
                      </Menu>
                    }
                    placement="bottomRight"
                  >
                    <Button block={true}>
                      Export
                    </Button>
                  </Dropdown>  
                )}
              </Col>
            </Row>

            {/* <Row type="flex" justify="start" align="top">
              <Col>
                <Tooltip
                  placement="bottom"
                  title="This report shows customer comments on returned products."
                >
                  <Icon style={{ paddingTop: "13px" }} type="info-circle" />
                </Tooltip>
              </Col>
              <Col>
                &nbsp;
                <span className="TextHeading TextHeading--sm">Customer Comments</span>
              </Col>
            </Row> */}

            {/* <Row type="flex" justify="center" align="middle">
              <Tooltip
                placement="top"
                title="Search for a customer by using their first name, last name or email address. Only customers with at least one Return in Rich Returns will show up as a result."
              >
                <Select
                  showSearch
                  value={this.props.customerNameEmail}
                  style={{ width: 350 }}
                  placeholder="Search for a customer"
                  onChange={onChange}
                  onSearch={(value) => this.props.onSearch(value)}
                  filterOption={false}
                >
                  {this.props.matchingCustomers &&
                  this.props.matchingCustomers.length > 0
                    ? this.props.matchingCustomers.map((value) => {
                      return (
                        <Option value={value.customerId}>
                          {`${value.shippingName} (${value.customerEmail})`}
                        </Option>
                      );
                    })
                    : null}
                </Select>
              </Tooltip>
            </Row>
            <br /> */}
            <div style={{ overflowX: 'auto' }} ref={this.printRef}>
              <div style={{ minWidth: 768 }}>
                <Table
                  dataSource={dataSource}
                  columns={columns}
                  locale={{
                    emptyText: (
                      <div className="flex-row flex-middle-xxs flex-center-xxs">
                        <div className="flex-col-xxs-12">
                          <IconTray size="md" />
                        </div>
                        <div>
                          <p className="TextBody TextBody--xxxs">No Data</p>
                        </div>
                      </div>
                    ),
                  }}
                />
              </div>
            </div>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <Row type="flex" justify="space-around" align="top">
              <Col span={24}>
                <Divider />
              </Col>
              <Upgrade
                title="Business Intelligence at your fingertips."
                description="Choose a plan with access to Reports. Explore insights into why customers return products and make smarter business decisions."
                videoId="VjbdXeknVMM"
              />
            </Row>
          </React.Fragment>
        )}
      </React.Fragment>
    );
  }
}

export default Comments;
